/**
 * orderDateUtils.js
 * Extracts dispatch and delivery dates from OrderDesk orders.
 * These dates are USER-SET in OrderDesk (checkout_data.Dispatch, checkout_data.Delivery),
 * not calculated. Use these helpers for consistent access across toolkits.
 */

import { parseDDMMYYYY, formatYYYYMMDD } from '../services/dateService.js';

/**
 * Get the raw dispatch date from an order (user-set in OrderDesk checkout_data).
 * @param {object} order - Order object (from OrderDesk API or Firestore)
 * @returns {string|undefined} Raw date string (DD-MM-YYYY or YYYY-MM-DD) or undefined
 */
export function getOrderDispatchDate(order) {
    if (!order) return undefined;
    const cd = order.checkout_data || order.checkoutData || {};
    return cd.Dispatch || cd.dispatch || cd.dispatch_date || cd['Dispatch Date']
        || order.dispatch_date || order.dispatchDate || undefined;
}

/**
 * Get the raw delivery date from an order (user-set in OrderDesk checkout_data).
 * @param {object} order - Order object (from OrderDesk API or Firestore)
 * @returns {string|undefined} Raw date string (DD-MM-YYYY or YYYY-MM-DD) or undefined
 */
export function getOrderDeliveryDate(order) {
    if (!order) return undefined;
    const cd = order.checkout_data || order.checkoutData || {};
    return cd.Delivery || cd.delivery || cd.delivery_date || cd['Delivery Date']
        || order.delivery_date || order.deliveryDate || undefined;
}

/**
 * Normalize a date string to YYYY-MM-DD for consistent comparison/sorting.
 * @param {string} dateStr - Date in DD-MM-YYYY, YYYY-MM-DD, or ISO format
 * @returns {string|null} YYYY-MM-DD or null if invalid
 */
export function normalizeOrderDate(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const trimmed = dateStr.trim();
    if (!trimmed) return null;
    const parsed = parseDDMMYYYY(trimmed) || new Date(trimmed);
    if (!parsed || isNaN(parsed.getTime())) return null;
    return formatYYYYMMDD(parsed);
}

/**
 * Enrich an order with normalized deliveryDate and dispatchDate from checkout_data.
 * Mutates the order in place and returns it.
 * @param {object} order - Order object
 * @returns {object} Order with deliveryDate and dispatchDate added
 */
export function enrichOrderWithDates(order) {
    if (!order) return order;
    const dispatchRaw = getOrderDispatchDate(order);
    const deliveryRaw = getOrderDeliveryDate(order);
    if (dispatchRaw) {
        order.dispatchDate = normalizeOrderDate(dispatchRaw) || dispatchRaw;
    }
    if (deliveryRaw) {
        order.deliveryDate = normalizeOrderDate(deliveryRaw) || deliveryRaw;
    }
    return order;
}
